import { buildApiUrl, getAuthHeaders } from '@/config/api';

export interface DashboardStats {
  totalDeals: number;
  totalDealsChange: number;
  inProgressDeals: number;
  completedDeals: number;
  completedDealsThisQuarter: number;
  riskAlerts: number;
  totalValue: string;
  totalValueNote: string;
  statusDistribution: { inProgress: number; completed: number; draft: number };
  riskDistribution: { high: number; medium: number; low: number };
  stageCounts: {
    intakeCount: number;
    docsSubmittedCount: number;
    underReviewCount: number;
    approvalCount: number;
    completedCount: number;
  };
}

export interface RecentDealActivity {
  id: string;
  title: string;
  type: string;
  status: string;
  value: number | null;
  currency: string | null;
  progress: number | null;
  riskLevel: string | null;
  expectedClosingDate: string | null;
  updatedAt: string;
  industry: string | null;
  buyer: string | null;
  seller: string | null;
  documentsCount: number;
  reportsCount: number;
}

export interface DealActivityPoint {
  month: string;
  deals: number;
  value: number;
}

export interface DealTypeMonthlyPoint {
  month: string;
  MA: number;
  DEBT_FINANCING: number;
  INVESTMENT: number;
}

export interface PerformanceMetrics {
  current: { dealsClosed: number; avgDealSize: number; successRate: number; timeToClose: number; };
  previous:{ dealsClosed: number; avgDealSize: number; successRate: number; timeToClose: number; };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

const API_BASE_URL = buildApiUrl('/dashboard');

class DashboardService {
  async getDashboardStats(): Promise<DashboardStats> {
    const res = await fetch(`${API_BASE_URL}/stats`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ApiResponse<DashboardStats> = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch dashboard stats');
    return json.data;
  }

  async getRecentDealActivity(
    limit = 10, search?: string, status?: string, type?: string, recentDays = 30
  ): Promise<RecentDealActivity[]> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('recentDays', String(recentDays));
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (type) params.append('type', type);

    const res = await fetch(`${API_BASE_URL}/recent-deals?${params.toString()}`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ApiResponse<RecentDealActivity[]> = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch recent deals');
    return json.data;
  }

  async getDealActivityTrend(): Promise<DealActivityPoint[]> {
    const res = await fetch(`${API_BASE_URL}/activity-trend`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ApiResponse<DealActivityPoint[]> = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch activity trend');
    return json.data;
  }

  // grouped monthly deal-type counts
  async getDealTypeTrend(): Promise<DealTypeMonthlyPoint[]> {
    const res = await fetch(`${API_BASE_URL}/deal-type-trend`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ApiResponse<DealTypeMonthlyPoint[]> = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch deal type trend');
    return json.data;
  }

  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    const res = await fetch(`${API_BASE_URL}/performance-metrics`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ApiResponse<PerformanceMetrics> = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch performance metrics');
    return json.data;
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
